// Airport (IATA) -> IANA timezone lookup, used to label the departure/arrival
// clock times in the flight listing with their real UTC offset
// (e.g. "06:30 (GMT+5:30)").
//
// Why this exists: the backend bakes each leg's LOCAL clock time into the ISO
// `departure`/`arrival` value (the UI renders them with timeZone: "UTC"), but it
// returns no timezone/offset, only the IATA airport code. So the numbers are the
// correct local times — they just have no label saying which zone. This map adds
// that label on the frontend.
//
// Coverage is curated to the busy international airports staff travellers actually
// use. Unknown codes degrade gracefully: getAirportOffsetLabel returns "" and the
// caller renders no label (same as today).

// Curated IATA -> IANA. Extend as needed; an absent code simply shows no label.
const AIRPORT_TIMEZONES: Record<string, string> = {
  // Indian subcontinent
  DEL: "Asia/Kolkata",
  BOM: "Asia/Kolkata",
  BLR: "Asia/Kolkata",
  MAA: "Asia/Kolkata",
  HYD: "Asia/Kolkata",
  CCU: "Asia/Kolkata",
  COK: "Asia/Kolkata",
  CCJ: "Asia/Kolkata",
  GOI: "Asia/Kolkata",
  AMD: "Asia/Kolkata",
  PNQ: "Asia/Kolkata",
  TRV: "Asia/Kolkata",
  IXE: "Asia/Kolkata",
  TRZ: "Asia/Kolkata",
  IXM: "Asia/Kolkata",
  VTZ: "Asia/Kolkata",
  NAG: "Asia/Kolkata",
  LKO: "Asia/Kolkata",
  PAT: "Asia/Kolkata",
  IXB: "Asia/Kolkata",
  IXC: "Asia/Kolkata",
  ATQ: "Asia/Kolkata",
  IXR: "Asia/Kolkata",
  BBI: "Asia/Kolkata",
  IDR: "Asia/Kolkata",
  RPR: "Asia/Kolkata",
  GAU: "Asia/Kolkata",
  IXJ: "Asia/Kolkata",
  SXR: "Asia/Kolkata",
  UDR: "Asia/Kolkata",
  JAI: "Asia/Kolkata",
  VNS: "Asia/Kolkata",
  IXZ: "Asia/Kolkata",
  STV: "Asia/Kolkata",
  CMB: "Asia/Colombo",
  KHI: "Asia/Karachi",
  LHE: "Asia/Karachi",
  ISB: "Asia/Karachi",
  DAC: "Asia/Dhaka",
  KTM: "Asia/Kathmandu",

  // Middle East
  DXB: "Asia/Dubai",
  AUH: "Asia/Dubai",
  SHJ: "Asia/Dubai",
  RAK: "Asia/Dubai",
  FJR: "Asia/Dubai",
  DOH: "Asia/Qatar",
  BAH: "Asia/Bahrain",
  KWI: "Asia/Kuwait",
  MCT: "Asia/Muscat",
  SLL: "Asia/Muscat",
  RUH: "Asia/Riyadh",
  JED: "Asia/Riyadh",
  DMM: "Asia/Riyadh",
  AMM: "Asia/Amman",
  BEY: "Asia/Beirut",
  TLV: "Asia/Jerusalem",
  IKA: "Asia/Tehran",
  BGW: "Asia/Baghdad",
  BSR: "Asia/Baghdad",
  NJF: "Asia/Baghdad",
  EBL: "Asia/Baghdad",

  // Europe
  LHR: "Europe/London",
  LGW: "Europe/London",
  STN: "Europe/London",
  MAN: "Europe/London",
  DUB: "Europe/Dublin",
  CDG: "Europe/Paris",
  ORY: "Europe/Paris",
  NCE: "Europe/Paris",
  FRA: "Europe/Berlin",
  MUC: "Europe/Berlin",
  DUS: "Europe/Berlin",
  BER: "Europe/Berlin",
  AMS: "Europe/Amsterdam",
  BRU: "Europe/Brussels",
  ZRH: "Europe/Zurich",
  GVA: "Europe/Zurich",
  VIE: "Europe/Vienna",
  MAD: "Europe/Madrid",
  BCN: "Europe/Madrid",
  LIS: "Europe/Lisbon",
  FCO: "Europe/Rome",
  MXP: "Europe/Rome",
  ATH: "Europe/Athens",
  IST: "Europe/Istanbul",
  SAW: "Europe/Istanbul",
  CPH: "Europe/Copenhagen",
  ARN: "Europe/Stockholm",
  OSL: "Europe/Oslo",
  HEL: "Europe/Helsinki",
  WAW: "Europe/Warsaw",
  PRG: "Europe/Prague",
  BUD: "Europe/Budapest",
  SVO: "Europe/Moscow",
  DME: "Europe/Moscow",
  LED: "Europe/Moscow",

  // Africa
  CAI: "Africa/Cairo",
  JNB: "Africa/Johannesburg",
  CPT: "Africa/Johannesburg",
  NBO: "Africa/Nairobi",
  ADD: "Africa/Addis_Ababa",
  LOS: "Africa/Lagos",
  CMN: "Africa/Casablanca",
  ALG: "Africa/Algiers",
  TUN: "Africa/Tunis",

  // North America
  JFK: "America/New_York",
  EWR: "America/New_York",
  LGA: "America/New_York",
  BOS: "America/New_York",
  IAD: "America/New_York",
  DCA: "America/New_York",
  ATL: "America/New_York",
  MIA: "America/New_York",
  MCO: "America/New_York",
  PHL: "America/New_York",
  CLT: "America/New_York",
  MLB: "America/New_York",
  ORD: "America/Chicago",
  DFW: "America/Chicago",
  IAH: "America/Chicago",
  MSP: "America/Chicago",
  DEN: "America/Denver",
  PHX: "America/Phoenix",
  LAX: "America/Los_Angeles",
  SFO: "America/Los_Angeles",
  SEA: "America/Los_Angeles",
  SAN: "America/Los_Angeles",
  LAS: "America/Los_Angeles",
  YYZ: "America/Toronto",
  YUL: "America/Toronto",
  YVR: "America/Vancouver",
  MEX: "America/Mexico_City",

  // South America
  GRU: "America/Sao_Paulo",
  GIG: "America/Sao_Paulo",
  EZE: "America/Argentina/Buenos_Aires",
  SCL: "America/Santiago",
  BOG: "America/Bogota",
  LIM: "America/Lima",

  // Asia Pacific
  SIN: "Asia/Singapore",
  KUL: "Asia/Kuala_Lumpur",
  BKK: "Asia/Bangkok",
  DMK: "Asia/Bangkok",
  HKT: "Asia/Bangkok",
  CGK: "Asia/Jakarta",
  DPS: "Asia/Makassar",
  MNL: "Asia/Manila",
  CEB: "Asia/Manila",
  HKG: "Asia/Hong_Kong",
  TPE: "Asia/Taipei",
  ICN: "Asia/Seoul",
  GMP: "Asia/Seoul",
  NRT: "Asia/Tokyo",
  HND: "Asia/Tokyo",
  KIX: "Asia/Tokyo",
  PEK: "Asia/Shanghai",
  PKX: "Asia/Shanghai",
  PVG: "Asia/Shanghai",
  CAN: "Asia/Shanghai",
  SZX: "Asia/Shanghai",
  HAN: "Asia/Ho_Chi_Minh",
  SGN: "Asia/Ho_Chi_Minh",

  // Oceania
  SYD: "Australia/Sydney",
  MEL: "Australia/Melbourne",
  BNE: "Australia/Brisbane",
  PER: "Australia/Perth",
  AKL: "Pacific/Auckland",
};

/** IANA timezone for an IATA airport code, or undefined if not in the map. */
export const getAirportTimezone = (code?: string | null): string | undefined => {
  if (!code) return undefined;
  return AIRPORT_TIMEZONES[code.trim().toUpperCase()];
};

// Cache formatters per timezone — building Intl.DateTimeFormat is comparatively
// expensive and the listing re-renders often.
const offsetFormatters: Record<string, Intl.DateTimeFormat> = {};

const getOffsetFormatter = (timeZone: string): Intl.DateTimeFormat => {
  if (!offsetFormatters[timeZone]) {
    offsetFormatters[timeZone] = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    });
  }
  return offsetFormatters[timeZone];
};

// "GMT+05:30" -> "GMT+5:30", "GMT-08:00" -> "GMT-8", "GMT" -> "GMT".
const normalizeOffset = (raw: string): string => {
  const match = raw.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return raw; // e.g. plain "GMT" (UTC)
  const [, sign, hours, minutes] = match;
  const h = String(parseInt(hours, 10));
  const m = minutes && minutes !== "00" ? `:${minutes}` : "";
  return `GMT${sign}${h}${m}`;
};

/**
 * UTC-offset label for an airport at the given date, DST-aware
 * (e.g. "GMT+5:30"). Returns "" when the airport timezone is unknown so callers
 * can render no label. `date` should be the leg's instant (any value new Date()
 * accepts); offset is resolved for that date so seasonal DST is reflected.
 */
export const getAirportOffsetLabel = (
  code?: string | null,
  date?: string | number | Date | null,
): string => {
  const timeZone = getAirportTimezone(code);
  if (!timeZone) return "";

  const d = date ? new Date(date) : new Date();
  if (isNaN(d.getTime())) return "";

  const part = getOffsetFormatter(timeZone)
    .formatToParts(d)
    .find((p) => p.type === "timeZoneName");
  if (!part) return "";

  return normalizeOffset(part.value);
};
